import { Component, Input, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { InputComponent } from "@components/input/input.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { Viol3statusIndicatorComponent } from "@components/viol3status-indicator/viol3status-indicator.component";
import { Observable } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { Anskaffningsformer, ConfirmDialogResponse, Produktionsstatus, Transportslag } from "~app/common/models/constants";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { ValidationContext } from "~app/common/validation/validationContext";
import { ArbetsorderAtgardService } from "~app/features/planera/arbetsorder-atgard/arbetsorder-atgard.service";
import * as ArbetsorderConstants from "~app/features/planera/model/ArbetsorderConstants";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { ArbetsorderMediatorService } from "~app/features/planera/services/arbetsorder-mediator.service";
import { ArbetsorderService } from "~app/features/planera/services/arbetsorder.service";
import { ArbetsorderViol3MakuleraButtonComponent } from "../../../arbetsorder-viol3/arbetsorder-viol3-makulera-button/arbetsorder-viol3-makulera-button.component";
import { AvslutaVirkesorderButtonComponent } from "../../../arbetsorder-virkesorder/avsluta-virkesorder-button/avsluta-virkesorder-button.component";
import { SetArbetsorderBatIdCommand } from "../../../commands/SetArbetsorderBatIdCommand";
import { ArbetsorderInfoSummaryComponent } from "../arbetsorder-info-summary/arbetsorder-info-summary.component";

@Component({
    selector: "rask-arbetsorder-info-summary-terminalimport",
    templateUrl: "./arbetsorder-info-summary-terminalimport.component.html",
    styleUrls: ["./arbetsorder-info-summary-terminalimport.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        InputComponent,
        FormsModule,
        TextmultilineComponent,
        SelectorComponent,
        Viol3statusIndicatorComponent,
        CheckboxComponent,
        AvslutaVirkesorderButtonComponent,
        ArbetsorderViol3MakuleraButtonComponent,
        ButtonComponent,
        RaskCommonModule
    ]
})
export class ArbetsorderInfoSummaryTerminalimportComponent extends ArbetsorderInfoSummaryComponent {
    readonly arbetsorderService: ArbetsorderService;

    @Input() arbetsorder: IArbetsorderDetail;
    arbetsorderNamnMaxLength = ArbetsorderConstants.ArbetsordernamnMaxLength;
    @Input() validationContext: ValidationContext;

    private readonly arbetsorderMediatorService = inject(ArbetsorderMediatorService);
    private readonly atgardService = inject(ArbetsorderAtgardService);
    private readonly busyService = inject(BusyService);
    private readonly raskModalService = inject(RaskModalService);

    constructor() {
        super();
    }

    avslutaArbetsorder(): void {
        this.raskModalService.confirm(new ModalSettings("Avsluta arbetsorder", "Vill du verkligen avsluta arbetsorder? Det går inte att ångra"))
            .subscribe((response) => {
                if (response === ConfirmDialogResponse.save) {
                    const worker: Observable<void> = this.busyService.addWork(
                        this.atgardService.avslutaArbetsorder(this.arbetsorder.id)
                    );
                    worker.subscribe((result) => {
                        this.arbetsorderMediatorService.reload();
                    });
                }
            });
    }

    batIdUpdated = () => {
        const cleanedBatId = this.arbetsorder.batId?.trim() ? this.arbetsorder.batId : null;
        const cmd = new SetArbetsorderBatIdCommand((instance) => {
            instance.arbetsorderId = this.arbetsorder.id;
            instance.batId = cleanedBatId;
        });
        this.arbetsorder.commandManager.updateCommand(cmd);
    };

    canAvslutaArbetsorder(): boolean {
        return this.arbetsorder?.produktionsstatus !== Produktionsstatus.ProduktionGodkand;
    }

    isAvgangsmatning() {
        return this.arbetsorder.avgangsmatning;
    }

    isImport() {
        return this.arbetsorder.anskaffningsform === Anskaffningsformer.Import;
    }

    isSjotransport() {
        return this.arbetsorder.transportslag === Transportslag.Sjo;
    }
}
